import { useEffect, useState } from "react";
import { request } from "../../../network/Request";
import { useTranslation } from "react-i18next";
import { useYear } from "../../../context/YearContext.js";
import StatusMessage from "../../../components/StatusMessage/StatusMessage.js";

// TODO 임시(2026-11-02): 서버에 특별점수를 넣을 수 없어 직접 지정. 서버 값이 있으면 서버 값 사용. 경기 끝나면 삭제
// 키: "연도-거리", 값: { 팀 이름(서버 team_name 그대로): 특별점수 }
const TEMP_SPECIAL_POINTS = {
  "2026-145": { "부산 D팀": 16, "세종 A팀": 8 },
};

const TeamView = ({ distance, targetType }) => {
  const { t } = useTranslation();
  const { year } = useYear();
  const [teamMemberArray, setTeamMemberArray] = useState([]);
  const [loading, setLoading] = useState(true);
  // 점수가 하나라도 있으면 경기 시작 (전에는 순위 없이 팀 목록만)
  const [started, setStarted] = useState(false);

  // 특별점수: 총점 동점 시 순위 결정 (TEAM.SPECIAL_POINT 직접 입력, 없으면 0으로 비교)
  const specialOf = (team) => team.special_point ?? 0;

  // 등수 계산 함수: 총점과 특별점수가 모두 같으면 같은 등수
  const calculateRank = (teams) => {
    let currentRank = 1;

    return teams.map((team, index) => {
      const prev = teams[index - 1];
      if (
        index > 0 &&
        (team.total !== prev.total || specialOf(team) !== specialOf(prev))
      ) {
        currentRank = index + 1;
      }
      return {
        ...team,
        rank: currentRank,
      };
    });
  };

  useEffect(() => {
    setLoading(true);
    request({
      "bk-working": "team-score-list",
      distance: distance,
      target_type: targetType,
      year: year,
    }).then(({ result, response }) => {
      if (result) {
        console.log(response);
        let teamScores = response["team_score_list"];
        console.log(teamScores);

        // 팀별로 그룹화
        const teamGroups = {};
        const tempSpecial = TEMP_SPECIAL_POINTS[`${year}-${distance}`] || {};

        teamScores.forEach((player) => {
          const teamName = player.team_name;
          if (!teamGroups[teamName]) {
            teamGroups[teamName] = {
              team_name: teamName,
              country: player.country,
              special_point:
                player.team_special_point ?? tempSpecial[teamName] ?? null,
              players: [],
            };
          }

          teamGroups[teamName].players.push({
            player_name: player.player_name,
            score: player.score ?? null, // 아직 점수가 없으면 null (2026 경기 전 목록)
            target_join_id: player.target_join_id,
          });
        });

        // 팀별 총점 계산 및 배열 변환
        const teams = Object.values(teamGroups).map((team) => {
          // 총점 계산
          const total = team.players.reduce(
            (sum, player) => sum + (player.score || 0),
            0
          );

          // 5명 초과 여부 확인
          const hasMoreThan5 = team.length > 5;
          if (hasMoreThan5) {
            console.warn(`⚠️ ${teams.team_name} 팀은 ${teams.length}명입니다.`);
          } else {
            console.log(`팀 정상`);
          }

          return {
            ...team,
            total: total,
          };
        });

        // 팀 총점 → 특별점수 내림차순 정렬
        teams.sort((a, b) => b.total - a.total || specialOf(b) - specialOf(a));

        // 총점이 같은 팀이 있고 특별점수가 입력된 팀만 합계 옆에 (특별점수) 표시
        const totalCount = {};
        teams.forEach((team) => {
          totalCount[team.total] = (totalCount[team.total] || 0) + 1;
        });
        teams.forEach((team) => {
          team.showSpecial =
            team.special_point != null && totalCount[team.total] > 1;
        });

        // 총점 동점을 특별점수로 가렸으면 동점 팀 중 맨 앞 팀에 WIN 표시
        teams.forEach((team, i) => {
          const prev = teams[i - 1];
          const next = teams[i + 1];
          team.winSpecial =
            (!prev || prev.total !== team.total) &&
            next?.total === team.total &&
            specialOf(team) > specialOf(next);
        });

        // 등수 계산
        const withRank = calculateRank(teams);

        setTeamMemberArray(withRank);
        setStarted(teamScores.some((player) => player.score != null));
      } else {
        setTeamMemberArray([]);
      }
      setLoading(false);
    });
  }, [distance, targetType, year]);

  return (
    <>
      <div className="table-box box-1 margin-table">
        <div className="table-wrap">
          <div className="table-shadow">
            {loading ? (
              <StatusMessage type="loading" />
            ) : teamMemberArray.length === 0 ? (
              <StatusMessage />
            ) : (
              <table>
                <colgroup>
                  <col width="5%" />
                  <col width="5%" />
                </colgroup>
                <thead>
                  <tr>
                    <th rowSpan="2">{t("table.rank")}</th>
                    <th rowSpan="2">{t("table.country")}</th>
                    <th rowSpan="2">{t("table.team-name")}</th>
                    <th colSpan="5">{t("table.individual-score")}</th>
                    <th rowSpan="2">{t("table.total")}</th>
                  </tr>
                  <tr>
                    <th>{t("positions.player")} 1</th>
                    <th>{t("positions.player")} 2</th>
                    <th>{t("positions.player")} 3</th>
                    <th>{t("positions.player")} 4</th>
                    <th>{t("positions.player")} 5</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMemberArray.map((team, index) => (
                    <tr className={started && team.rank <= 3 ? "ranking" : ""} key={index}>
                      <td>{started ? team.rank : "-"}</td>
                      <td>{team.country}</td>
                      <td>
                        {team.team_name}
                        {started && team.winSpecial && (
                          <span className="team-win">WIN</span>
                        )}
                      </td>
                      {/* 선수 1~5의 점수 표시 */}
                      {[0, 1, 2, 3, 4].map((playerIndex) => (
                        <td key={playerIndex}>
                          {team.players[playerIndex] ? (
                            <>
                              {team.players[playerIndex].score ?? "-"}
                              <br />({team.players[playerIndex].player_name})
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      ))}
                      <td className="sum">
                        {team.total}
                        {started && team.showSpecial && ` (${team.special_point})`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="table-gradient"></div>
        </div>
      </div>
    </>
  );
};
export default TeamView;
